import { redirect } from 'react-router-dom';

// Constants
import { formOrderNaming } from '../../../../../../components/forms/NewWorkOrderForm/constants';

// GraphQL
import { client } from '../../../../../../gql/client';
import { SAVE_GENERIC_ORDER } from '../../../../../../gql/mutations/generic-order';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';
import {
  prependCampaignToCache,
  prependGenericOrderToCache,
} from '../../../../../../gql/cacheHelpers';

// Types
import { OrderState, OrderType } from '../../../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

//Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';


// REDUX
import { store } from '../../../../../../state/configureStore';
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../../../state/selectors/ui/tables';

export const saveData = async (dataFormRaw: string | null) => {
  let dataOrder;
  const dispatch = store.dispatch;
  // Cursors for campaigns
  const cursorCampaignModal = getCursor(tableIds.CAMPAIGN_MODAL)(
    store.getState()
  );
  const cursorCampaigns = getCursor(tableIds.CAMPAIGNS)(store.getState());

  // Cursors for Work orders
  const cursorWorkOrdersGeneralView = getCursor(
    tableIds.WORKORDER_GENERAL_VIEW
  )(store.getState());
  const cursorDuplicateGenericOrder = getCursor(
    tableIds.DUPLICATE_GENERIC_ORDER
  )(store.getState());
  const cursorSearchAndCreateWorkOrders = getCursor(
    tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS
  )(store.getState());
  const cursorGenericModal = getCursor(tableIds.GENERIC_ORDER_MODAL)(
    store.getState()
  );

  const creationDate = new Date().toISOString();

  if (dataFormRaw) {
    const dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));
    const pkInitArray = dataForm.pkInit.replace(/ /g, '').split('+');
    const pkEndArray = dataForm.pkEnd.replace(/ /g, '').split('+');

    if (dataForm.isCampaignActive !== 'true') {
      // Bloque C-2: SAVE_GENERIC_ORDER sustituye CREATE_NEW_ORDER + CREATE_NEW_GENERIC_ORDER
      // + UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS por una mutación atómica.
      // Backend genera ROID, indexExpedition/indexType e infiere todos los Tipo B
      // denormalizados a la Order paralela.
      dataOrder = await client.mutate({
        mutation: SAVE_GENERIC_ORDER,
        variables: {
          input: {
            // Idempotencia online: UUID estable por operación (RetryLink no duplica).
            clientOpId: crypto.randomUUID(),
            state: OrderState.Open,
            creationDate,
            registerInitDate: dataForm.registerInitDate,
            pkInitKm: Number(pkInitArray[0]),
            pkInitMeter: Number(pkInitArray[1]),
            pkEndKm: Number(pkEndArray[0]),
            pkEndMeter: Number(pkEndArray[1]),
            track: dataForm.track,
            direction: dataForm?.direction,
            margin: dataForm?.margin,
            lane: dataForm.lane,
            concessionId: dataForm.concession,
            roadId: dataForm.road,
            teamIds: Array.isArray(dataForm.teamRelationships)
              ? dataForm.teamRelationships
              : dataForm.teamRelationships
                ? String(dataForm.teamRelationships).split(',').map((s: string) => s.trim()).filter(Boolean)
                : [],
            capitolId: dataForm.capitol,
            subCapitolId: dataForm.subCapitol,
            classification: dataForm.classification,
            ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
            description: dataForm.description,
            notes: dataForm.notes,
          },
        },
        update(cache, { data: { saveGenericOrder } }) {
          // Cache prepend reutilizable (el mismo helper se usa desde
          // syncOfflineQueue al replay-ear offline).
          prependGenericOrderToCache(cache, saveGenericOrder.genericOrder);
          // Side effect online: mover al usuario a página 1 al volver a la
          // lista para que vea lo recién creado arriba. NO se replica en
          // el sync offline (preserva paginación del usuario). Solo si la
          // cache ya tenía datos — evita resets innecesarios.
          if (cursorWorkOrdersGeneralView?.cursor !== undefined) {
            dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, {
              ...cursorWorkOrdersGeneralView, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, {
              ...cursorDuplicateGenericOrder, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL, {
              ...cursorGenericModal, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, {
              ...cursorSearchAndCreateWorkOrders, take: takeBase, cursor: '', orderBy: 'desc',
            }));
          }
        },
      });
    } else {
      // Bloque C-3: SAVE_CAMPAIGN sustituye CREATE_NEW_ORDER + CREATE_NEW_CAMPAIGN
      // (+ UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS) por una mutación atómica.
      dataOrder = await client.mutate({
        mutation: SAVE_CAMPAIGN,
        variables: {
          input: {
            // Idempotencia: congela el opId en las variables → RetryLink/offline
            // reintentan con el mismo id y el backend deduplica (no crea 2ª OT).
            clientOpId: crypto.randomUUID(),
            state: OrderState.Open,
            creationDate,
            registerInitDate: dataForm.registerInitDate,
            pkInitKm: Number(pkInitArray[0]),
            pkInitMeter: Number(pkInitArray[1]),
            pkEndKm: Number(pkEndArray[0]),
            pkEndMeter: Number(pkEndArray[1]),
            track: dataForm.track,
            direction: dataForm?.direction,
            margin: dataForm?.margin,
            lane: dataForm.lane,
            concessionId: dataForm.concession,
            roadId: dataForm.road,
            teamIds: Array.isArray(dataForm.teamRelationships)
              ? dataForm.teamRelationships
              : dataForm.teamRelationships
                ? String(dataForm.teamRelationships).split(',').map((s: string) => s.trim()).filter(Boolean)
                : [],
            capitolId: dataForm.capitol,
            subCapitolId: dataForm.subCapitol,
            classification: dataForm.classification,
            ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
            description: dataForm.description,
            notes: dataForm.notes,
            name: dataForm.campaign,
          },
        },
        update(cache, { data: { saveCampaign } }) {
          prependCampaignToCache(cache, saveCampaign.campaign);
          if (cursorCampaigns?.cursor !== undefined) {
            dispatch(setCursor(tableIds.CAMPAIGN_MODAL, {
              ...cursorCampaignModal, take: takeBase, cursor: '', orderBy: 'desc',
            }));
            dispatch(setCursor(tableIds.CAMPAIGNS, {
              ...cursorCampaigns, take: takeBase, cursor: '', orderBy: 'desc',
            }));
          }
        },
      });
    }

    // Bloque C-2/C-3: normaliza el shape — saveGenericOrder.order o saveCampaign.order.
    const createdOrder =
      dataOrder?.data?.saveGenericOrder?.order ?? dataOrder?.data?.saveCampaign?.order;

    // Refetch
    if (createdOrder?.id) {
      await client.query({
        fetchPolicy: 'network-only',
        query: GET_ORDER_BY_ID,
        variables: { id: createdOrder.id },
      });
    }

    // Clean the form local storage as it was just saved
    sessionStorage.removeItem(formOrderNaming.NEW_WORK_ORDER_FORM);
    return createdOrder?.type === OrderType.Generic
      ? redirect(`../${createdOrder.id}`)
      : redirect(`../campaign/${createdOrder.id}`);
  }

  // Sin dataFormRaw, no hay nada que guardar. Vuelve atrás.
  sessionStorage.removeItem(formOrderNaming.NEW_WORK_ORDER_FORM);
  return redirect('..');
};
